import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AddressSuggestion {
  id: string;
  text: string;
  type: "address" | "city" | "zip" | "county" | "neighborhood";
  city: string;
  state: string;
  zip: string;
  address: string;
  lat?: number;
  lng?: number;
}

interface AutocompleteResult {
  suggestions: AddressSuggestion[];
  error?: string;
}

/** Minimum characters before we ask the API for anything. */
const MIN_QUERY_LENGTH = 2;

const DEBOUNCE_MS = 300;

/**
 * Hard ceiling on a single request. Without it a hung edge function (cold start,
 * saturated worker pool, slow upstream) leaves the promise unsettled forever, so
 * `finally` never runs and the spinner never stops.
 */
const REQUEST_TIMEOUT_MS = 8000;

export function useAddressAutocomplete() {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortControllerRef = useRef<AbortController>();
  /**
   * Monotonic id of the newest request. Responses arrive out of order — six
   * keystrokes produced completions in the order birmi, bi, birmin, birm, bir —
   * so a response may only touch state if it is still the current one.
   */
  const requestIdRef = useRef(0);

  const fetchSuggestions = useCallback(async (query: string) => {
    // Cancel any pending request. This only does anything because the signal is
    // handed to invoke() below; previously the controller was created but never
    // passed, so every keystroke's request ran to completion.
    abortControllerRef.current?.abort();

    const trimmed = query.trim();
    const requestId = ++requestIdRef.current;

    if (trimmed.length < MIN_QUERY_LENGTH) {
      abortControllerRef.current = undefined;
      setSuggestions([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } =
        await supabase.functions.invoke<AutocompleteResult>(
          "address-autocomplete",
          {
            body: { query: trimmed, types: ["address", "city", "zip"] },
            signal: controller.signal,
            timeout: REQUEST_TIMEOUT_MS,
          },
        );

      if (requestId !== requestIdRef.current) return;

      if (fnError) {
        setSuggestions([]);
        setError("Couldn't load suggestions");
        return;
      }

      setSuggestions(data?.suggestions ?? []);
      // The function returns 200 with an `error` field on upstream failure, so an
      // empty list alone can't tell "no matches" from "the call broke".
      setError(data?.error ? "Couldn't load suggestions" : null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;

      // A superseded request and a timed-out one both surface as AbortError —
      // supabase-js implements `timeout` with its own AbortController.
      const wasAborted = err instanceof Error && err.name === "AbortError";

      setSuggestions([]);
      setError(wasAborted ? "Suggestions timed out" : "Couldn't load suggestions");
    } finally {
      // Only the newest request owns the spinner. A stale one finishing must not
      // switch it off while the current query is still in flight.
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const debouncedFetch = useCallback(
    (query: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, DEBOUNCE_MS);
    },
    [fetchSuggestions],
  );

  const clearSuggestions = useCallback(() => {
    // Bump the id so an in-flight response can't repopulate the list after the
    // user has picked something.
    requestIdRef.current++;
    abortControllerRef.current?.abort();
    setSuggestions([]);
    setError(null);
    setIsLoading(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      // A counter, not a node ref. Reading its value at teardown is precisely the
      // intent: bump the id so any in-flight response becomes a no-op.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      requestIdRef.current++;
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    fetchSuggestions: debouncedFetch,
    clearSuggestions,
  };
}
